<template>
  <div v-if="showHowToStyleIt" class="how-to-style-it" :class="classes">
    <div class="container">
      <div class="heading">
        <h2 class="title">How To Style It</h2>
        <p class="intro">
          Styling notes by our Creative Directors Valentina and Stephanie
        </p>
      </div>

      <div class="grid">
        <div class="media">
          <Media
            :sources="product.howToStyleItMedia.reference.sources"
            :images="product.howToStyleItMedia.reference.image"
          />
        </div>
        <div class="summary">
          <form class="form" @submit.prevent="handleFormSubmit">
            <div class="product-list">
              <StyleProduct
                v-for="(product, index) in orderedProducts"
                :key="product.id"
                :product="product"
                :class="`product product-${index}`"
                :orientation="productOrientation"
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import productIdQuery from "@/graphql/queries/ProductId.gql";
import { mapState } from "vuex";
import cloneDeep from "lodash-es/cloneDeep";
import StyleProduct from "@/components/product-display/HowToStyleItProduct.vue";
import Media from "@/components/Media.vue";

export default {
  name: "HowToStyleIt",
  components: {
    Media,
    StyleProduct,
  },
  inject: ["mq"],
  data() {
    return {
      products: [],
    };
  },
  computed: {
    ...mapState("product", ["product"]),
    classes() {
      return {
        "how-to-style-it--few-items": this.products.length <= 3,
      };
    },
    productId() {
      return this.product?.id.split("/Product/")[1];
    },
    orderedProducts() {
      const products = cloneDeep(this.products);
      return products.sort((a, b) => a.order - b.order);
    },
    showHowToStyleIt() {
      const hasProducts = this.products.length > 0;
      const hasMedia = this.product?.howToStyleItMedia?.reference;
      return hasProducts && hasMedia;
    },
    productOrientation() {
      if (this.products.length > 3) {
        return "landscape";
      }

      return this.mq.lgPlus ? "portrait" : "landscape";
    },
  },
  async created() {
    const groupedProductIds = await this.fetchChildProductIds();

    if (!groupedProductIds.length) {
      return false;
    }

    await this.getProducts(groupedProductIds);
  },
  methods: {
    async fetchChildProductIds() {
      if (!this.productId) {
        return;
      }

      const url = `${process.env.PRODUCT_GROUPING_GADGET_URL}/how-to-style-it/${this.productId}`;

      const resp = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!resp.ok) {
        throw new Error("Error fetching grouped products");
      }

      const data = await resp.json();

      return data?.products || [];
    },
    async getProducts(products) {
      const promises = products.map(({ id }) => {
        return this.$apollo.query({
          query: productIdQuery,
          variables: {
            productId: "gid://shopify/Product/" + id,
          },
        });
      });

      const values = await Promise.all(promises);
      values.forEach((value) => {
        const product = value.data.product;

        const order = products.find(
          (p) => "gid://shopify/Product/" + p.id === product.id
        ).order;

        this.products.push({ ...product, order });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.how-to-style-it {
  background-color: $color-grey-lightest;
  padding: 40px 0 60px;

  @include respond-above(lg) {
    padding: 60px 0;
  }

  .container {
    max-width: 448px;

    @include respond-above(lg) {
      max-width: 780px;
    }

    @include respond-above(xl) {
      max-width: 980px;
    }
  }
}

.heading {
  text-align: center;
  margin-bottom: 40px;

  @include respond-above(lg) {
    margin-bottom: 60px;
  }

  .title {
    @include editorial-headline(24px);
    text-transform: uppercase;
    margin-bottom: 16px;

    @include respond-above(lg) {
      @include editorial-headline(28px);
    }
  }

  .intro {
    @include editorial-body(14px);
  }
}

.product-list {
  .product {
    padding-bottom: 20px;
    margin-bottom: 20px;
    border-bottom: 1px solid $color-grey;
  }
}

.how-to-style-it--few-items {
  .grid {
    @include respond-above(lg) {
      display: grid;
      grid-template-columns: 457fr 213fr;
      gap: 30px;
    }

    @include respond-above(xl) {
      gap: 80px;
      grid-template-columns: 524fr 290fr;
    }
  }
}

.grid {
  @include respond-above(lg) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 46px;
  }
}

.media {
  margin-bottom: 20px;
  position: relative;

  :deep(.wrapper) {
    position: sticky;
    top: 150px;
  }

  @include respond-above(lg) {
    margin-bottom: 0;
  }
}

.title {
  @include editorial-headline(32px);

  @include respond-above(lg) {
    @include editorial-headline(40px);
  }
}
</style>
